import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { UPDATE_CONTACTS_MUTATION } from 'Queries/Mutations';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { CustomModal } from 'components/CustomModal';
import { DEBExpertPopupContactSection } from 'components/DebFormComponents/Popup/DEBExpertPopupContactSection';
import {
  EXPERT_CONTACT_ERRORS,
  buildExpertContactValidationFields,
  getExpertContactFormErrorMessage,
  getExpertEmails,
  submitExpertContactsUpdate,
} from 'components/DebFormComponents/Popup/expertContact.utils';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';

import { FFormDebug } from 'utils/FFormDebug';

import './ExtendExpertPopup.sass';
import './Popup.sass';

export const DebExpertPopupEditContacts = ({
  mainFormik,
  selectedRow,
  popupOpen,
  setPopupOpen,
  setSelectedRow,
  onExpertContactSaved,
}) => {
  const [apiError, setApiError] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const expert = mainFormik.values.experts?.[selectedRow] ?? null;
  const normalizedCurrentEmail = (expert?.netzportalEmail || '').trim().toLowerCase();
  const expertsEmails = useMemo(
    () => getExpertEmails(mainFormik.values.experts),
    [mainFormik.values.experts]
  );
  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        buildExpertContactValidationFields({
          forbiddenMails: expertsEmails,
          normalizedCurrentEmail,
          isEditing: true,
        })
      ),
    [expertsEmails, normalizedCurrentEmail]
  );

  const formik = useFormik({
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      netzportalEmail: expert?.netzportalEmail || '',
      newEmail: '',
      confirmEmail: '',
      telefon: expert?.telefon || '',
    },
  });

  const [updateContactsMutation, { loading: isExpertUpdating }] =
    useMutation(UPDATE_CONTACTS_MUTATION);
  useEffect(() => {
    if (!popupOpen || !expert) {
      return;
    }

    const nextValues = {
      netzportalEmail: expert.netzportalEmail || '',
      newEmail: '',
      confirmEmail: '',
      telefon: expert.telefon || '',
    };
    formik.setValues(nextValues, true);
  }, [popupOpen, expert?.id]);

  const onCancel = () => {
    if (updateSuccess) {
      mainFormik.setFieldValue(
        `experts[${selectedRow}]`,
        {
          ...expert,
          netzportalEmail: formik.values.netzportalEmail,
          telefon: formik.values.telefon,
        },
        false
      );
      onExpertContactSaved?.({
        id: expert.id,
        telefon: formik.values.telefon,
        netzportalEmail: formik.values.netzportalEmail,
      });
    }
    formik.resetForm();
    setSelectedRow(null);
    setPopupOpen(false);
    setUpdateSuccess(false);
    setApiError(false);
  };

  const onSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0 || !expert) {
      return;
    }

    submitExpertContactsUpdate(updateContactsMutation, expert.id, formik.values, {
      isEditing: true,
    })
      .then((result) => {
        if (result.duplicateEmail) {
          setApiError(EXPERT_CONTACT_ERRORS.DUPLICATE_EMAIL);
          return;
        }

        if (result.needsEmailConfirmation) {
          setUpdateSuccess(true);
          return;
        }

        setUpdateSuccess(true);
      })
      .catch(() => {
        setApiError(EXPERT_CONTACT_ERRORS.UPDATE_FAILED);
        console.error('Error updating contacts');
      });
  };

  const modalContent = () => {
    if (!expert) {
      return (
        <div className="popup-content">
          <ErrorBanner>Kein Eintrag ausgewählt</ErrorBanner>
        </div>
      );
    }

    if (updateSuccess) {
      return (
        <div className="popup-content">
          Vielen Dank. Ihre Kontaktdaten wurden gespeichert.
          {formik.values.newEmail && (
            <>
              <br />
              Es wird ein neuer Link für die Passwort-Vergabe zum Account von{' '}
              <strong>{expert.completeName}</strong> an die neue E-Mail-Adresse{' '}
              <strong>{formik.values.newEmail}</strong> verschickt.
            </>
          )}
        </div>
      );
    }

    if (apiError) {
      return (
        <div className="popup-content">
          <ErrorBanner>{apiError}</ErrorBanner>
        </div>
      );
    }

    return (
      <div className="popup-content">
        <div className="popup-content__popup-subtext extend-expert-popup__subtitle">
          Kontaktdaten für die Fachkraft{' '}
          <span className="edit-popup-expert-name">{expert.completeName}</span> bearbeiten.
        </div>
        <DEBExpertPopupContactSection formik={formik} isEditing />
        <FFormDebug values={formik.values} errors={formik.errors} touched={formik.touched} />
      </div>
    );
  };

  const modalButtons = () => {
    if (apiError) {
      return <ActionButton inverted onClick={onCancel} title="OK" />;
    }

    if (updateSuccess) {
      return <ActionButton inverted onClick={onCancel} title="OK" />;
    }

    return (
      <FormActionButtons
        hasErrors={Object.keys(formik.errors).length > 0}
        titleLeft="Abbrechen"
        titleRight="Speichern"
        onClickLeft={onCancel}
        onClickRight={onSubmit}
        loading={isExpertUpdating}
        errorMessage={getExpertContactFormErrorMessage(formik.errors)}
      />
    );
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Kontaktdaten bearbeiten"
      content={modalContent()}
      closeCancelEventModal={onCancel}
      ctaButtons={modalButtons()}
    />
  );
};

DebExpertPopupEditContacts.propTypes = {
  mainFormik: PropTypes.object.isRequired,
  selectedRow: PropTypes.number,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  setSelectedRow: PropTypes.func,
  onExpertContactSaved: PropTypes.func,
};

DebExpertPopupEditContacts.defaultProps = {
  popupOpen: false,
  setPopupOpen: () => {},
  setSelectedRow: () => {},
  selectedRow: null,
  onExpertContactSaved: undefined,
};
